import React, {useState, useEffect, useRef, useCallback} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'

// Hooks
import {useProductList} from '../util/product-list-context'
import {useWishlistActions} from '@app/hooks/use-wishlist-actions'
import useShipToCountry from '../../../hooks/use-ship-to-country'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import useEinstein from '../../../hooks/use-einstein'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {Box, Button, SimpleGrid, Link} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../../components/product-tile'
import MetaTagsComponent from '../../../components/metatags'
import PaginationMetaTags from '../partials/pagination-metatags'
// Constants
import {
    DEFAULT_SEARCH_PARAMS,
    SEARCH_PARAM_LOAD_MORE_LIMIT,
    PRODUCT_SEARCH_PRODUCT_TYPE_REFINEMENT
} from '../../../constants'

// Utils
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'

// Custom utils
import {buildMetaData} from '../../../components/metatags/util/metaDataUtils'
import {
    getPageIndex,
    getLimitForPage,
    getOffsetForPage,
    getTotalPages,
    buildPageUrl
} from '@app/utils/seo-utils'

/**
 * A React component that renders a subset of a product list in a classic grid layout.
 * This component handles fetching and displaying products, managing loading states,
 * and integrating with wishlist functionality.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.subSet - The initial subset of products to display.
 * @param {Object} props.props - Additional properties to be spread onto the root component.
 * @returns {JSX.Element} A React component that displays a grid of product tiles with
 *                        functionality to load more products and manage wishlist items.
 *
 * @example
 * <ProductListClassicSubset subSet={initialProducts} />
 *
 * @component
 * @category ProductList
 * @subcategory ClassicSubset
 *
 * @hook useIntl - Provides internationalization capabilities.
 * @hook useEinstein - Provides integration with Einstein for tracking product interactions.
 * @hook useShipToCountry - Retrieves the current shipping country.
 * @hook useProductList - Provides product list context including search and category details.
 * @hook useProductListWishlist - Manages wishlist actions and state.
 * @hook useRef - Creates references for DOM elements.
 * @hook useState - Manages component state for products, fetching states, and offsets.
 * @hook useSearchParams - Constructs search parameters for product fetching.
 * @hook useProductSearch - Fetches products based on search parameters.
 * @hook useEffect - Handles side effects such as updating states and focusing elements.
 * @hook useCallback - Memoizes the product click handler to optimize performance.
 *
 * @function handleTileLoad - Checks if all product tiles are loaded and updates state accordingly.
 * @function handleLoadMore - Increases the offset and triggers fetching of more products.
 * @function memoizedHandleProductClick - Sends click events to Einstein for tracking.
 */
const ProductListClassicSubset = ({subSet, ...props}) => {
    const intl = useIntl()
    const einstein = useEinstein()
    const {shipToCountry, isReady} = useShipToCountry()
    const {
        isSearch,
        category,
        total,
        isLoading: isPageProductListFetching,
        isRefetching: isPageProductListRefetching,
        isFetched: isPageProductListFetched,
        searchQuery,
        metatags
    } = useProductList()
    const {wishlist, addProductToWishlist, removeProductFromWishlist} = useWishlistActions()

    // Refs
    const tilesGrid = useRef(null)
    const firstNewProductRef = useRef(null)

    // Params
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    const rawPageParam = parseInt(urlParams.get('p'))
    const pageParam = getPageIndex(rawPageParam)

    // States
    const [products, setProducts] = useState(subSet)
    const [isFetching, setIsFetching] = useState(isPageProductListFetching)
    const [isRefetching, setIsRefetching] = useState(isPageProductListRefetching)
    const [isFetched, setIsFetched] = useState(isPageProductListFetched)

    const [shouldFetch, setShouldFetch] = useState(false)
    const [allTilesLoaded, setAllTilesLoaded] = useState(false)
    const [firstNewProductIndex, setFirstNewProductIndex] = useState(products?.length)
    const [currentSearchQuery, setCurrentSearchQuery] = useState('')
    const [isInitialLoad, setIsInitialLoad] = useState(true)

    // Params for products fetch
    const limitValue = getLimitForPage(pageParam)
    const initialOffset = getOffsetForPage(pageParam)
    const [offset, setOffset] = useState(initialOffset)
    const [searchParams] = useSearchParams({
        ...DEFAULT_SEARCH_PARAMS,
        limit: shouldFetch ? SEARCH_PARAM_LOAD_MORE_LIMIT : limitValue,
        offset: offset
    })
    const nextPageParam =
        pageParam + 1 < getTotalPages(total) ? buildPageUrl(location, pageParam + 2) : null
    const previousPageParam = pageParam === 0 ? null : buildPageUrl(location, pageParam)

    if (!isSearch) {
        searchParams._refine.push(`cgid=${category?.id}`)
    }

    // Only retrieve variation groups and sets (exclude other product types)
    searchParams._refine.push(PRODUCT_SEARCH_PRODUCT_TYPE_REFINEMENT)

    const {_refine, ...restOfParams} = searchParams

    // Products fetch
    const {
        isFetched: isSubsetFetched,
        isFetching: isSubsetFetching,
        isRefetching: isSubsetRefetching,
        data: productSearchResult
    } = useProductSearch(
        {
            parameters: {
                ...restOfParams,
                limit: shouldFetch ? SEARCH_PARAM_LOAD_MORE_LIMIT : limitValue,
                allVariationProperties: true,
                allImages: true,
                expand: [
                    'promotions',
                    'variations',
                    'prices',
                    'images',
                    'custom_properties',
                    'page_meta_tags'
                ],
                refine: _refine,
                c_country: shipToCountry?.id,
                select: '(hits.(**))'
            }
        },
        {
            keepPreviousData: false,
            enabled: isReady && shouldFetch && searchParams?.limit && searchParams?.limit > 0
        }
    )

    // Update the fetching states when the subset changes.
    useEffect(() => {
        if (shouldFetch) {
            if (restOfParams.q !== currentSearchQuery) {
                setCurrentSearchQuery(restOfParams.q)
            }
            setIsFetching(isSubsetFetching)
            setIsRefetching(isSubsetRefetching)
            setIsFetched(isSubsetFetched)
        }
    }, [shouldFetch, isSubsetFetching, isSubsetRefetching, isSubsetFetched])

    // When a new fetch is done in the ProductListProvider, update the fetching states.
    useEffect(() => {
        setIsFetching(isPageProductListFetching)
        setIsRefetching(isPageProductListRefetching)
        setIsFetched(isSubsetFetched)
    }, [isPageProductListFetching, isPageProductListRefetching, isPageProductListFetched])

    // Once the products are fetched, update the products state.
    useEffect(() => {
        if (shouldFetch && productSearchResult) {
            setProducts((prev) => {
                setFirstNewProductIndex(prev.length)
                return isInitialLoad ? subSet : [...prev, ...(productSearchResult?.hits || [])]
            })
            setAllTilesLoaded(false) // Reset the loaded state
        }
    }, [productSearchResult])

    // After clicking on "Load more" and once all product tiles are loaded,
    // we give focus to the first one
    useEffect(() => {
        if (allTilesLoaded && firstNewProductRef.current) {
            firstNewProductRef.current.focus()
        }
    }, [allTilesLoaded])

    // Check if all ProductTiles are loaded
    const handleTileLoad = () => {
        if (products.length === tilesGrid.current.querySelectorAll('.product-tile').length) {
            setAllTilesLoaded(true)
        }
    }

    // Refresh the subsets when a filter is applied for example
    useEffect(() => {
        setProducts(subSet)
        setOffset(initialOffset)
        setIsInitialLoad(true)
    }, [subSet])

    // Trigger fetching when "Load More" is clicked
    const handleLoadMore = () => {
        setIsInitialLoad(false)
        setOffset(
            (currentOffset) =>
                currentOffset + (isInitialLoad ? limitValue : SEARCH_PARAM_LOAD_MORE_LIMIT)
        )
        setShouldFetch(true)
    }

    const nbRemainingProduct = Math.max(total - (products?.length || 0), 0)
    const memoizedHandleProductClick = useCallback((productSearchItem) => {
        if (searchQuery) {
            einstein.sendClickSearch(searchQuery, productSearchItem)
        } else if (category) {
            einstein.sendClickCategory(category, productSearchItem)
        }
    }, [])

    /*
     * Metadata constructed in the getProps method and passed to the MetaTags component.
     */
    const finalMetadata = buildMetaData(category, metatags, searchQuery)

    return (
        <Box {...(!isSearch && {borderBottomWidth: 1, borderColor: 'borderGray'})} {...props}>
            {total && <PaginationMetaTags total={total} />}

            <MetaTagsComponent searchQuery={searchQuery} metaData={finalMetadata} />
            <SimpleGrid
                as="ul"
                className="product-list-grid"
                listStyleType="none"
                columns={[2, 2, 3, 4, 4, 5]}
                spacingX="10px"
                spacingY={{base: 24, lg: 70}}
                ref={tilesGrid}
            >
                {isHydrated() && (((isRefetching || isFetching) && !isFetched) || !subSet)
                    ? new Array(20)
                          .fill(0)
                          .map((value, index) => <ProductTileSkeleton key={`skeleton-${index}`} />)
                    : products
                          // filter out products that don't have a color and size variation except for sets (sets don't have variations)
                          ?.filter(
                              (product) =>
                                  product?.variants?.every(
                                      (variant) =>
                                          variant?.variationValues?.size &&
                                          variant?.variationValues?.color
                                  ) || product?.hitType === 'set'
                          )
                          ?.map((productSearchItem, index) => {
                              const productId = productSearchItem?.productId
                              const isInWishlist = !!wishlist?.customerProductListItems?.find(
                                  (item) => item?.productId === productId
                              )
                              return (
                                  <Box key={productSearchItem?.productId} as="li">
                                      <ProductTile
                                          className="product-tile"
                                          data-testid={`sf-product-tile-${productSearchItem?.productId}`}
                                          product={productSearchItem}
                                          isFavourite={isInWishlist}
                                          isFavIconInTitle={true}
                                          isRefreshingData={
                                              (isRefetching || isFetching) && isFetched
                                          }
                                          onClick={() =>
                                              memoizedHandleProductClick(productSearchItem)
                                          }
                                          onFavouriteToggle={(toBeFavourite) => {
                                              const action = toBeFavourite
                                                  ? addProductToWishlist
                                                  : removeProductFromWishlist
                                              return action(productSearchItem)
                                          }}
                                          // Prevent horizontal overflow caused by image sizes that ignore grid gaps.
                                          dynamicImageProps={{
                                              widths: [
                                                  'calc((100vw - 10px) / 2)', // '50vw' => 2 columns: 1 gap
                                                  'calc((100vw - 10px) / 2)', // '50vw' => 2 columns: 1 gap
                                                  'calc((100vw - 20px) / 3)', // '33vw' => 3 columns: 2 gaps
                                                  'calc((100vw - 30px) / 4)', // '25vw' => 4 columns: 3 gaps
                                                  'calc((100vw - 30px) / 4)', // '25vw' => 4 columns: 3 gaps
                                                  'calc((100vw - 40px) / 5)' // '20vw' => 5 columns: 4 gaps
                                              ]
                                          }}
                                          listHasNoMargin={true}
                                          index={index}
                                          focusRef={
                                              index === firstNewProductIndex
                                                  ? firstNewProductRef
                                                  : null
                                          }
                                          onLoad={handleTileLoad}
                                          isImageLazyLoading={index > 4}
                                      />
                                  </Box>
                              )
                          })}
            </SimpleGrid>

            <Box textAlign={'center'} pb={['70px', null, null, '80px']}>
                {/*Load more button*/}
                {nbRemainingProduct > 0 && (
                    <Box textAlign="center" pb={['70px', null, null, '80px']}>
                        <Button
                            variant="link"
                            mt={['70px', null, null, '80px']}
                            aria-label={intl.formatMessage(
                                {
                                    defaultMessage: 'Load {nbRemainingProduct} additional products',
                                    id: 'product_list.pagination.load_more.arialabel'
                                },
                                {nbRemainingProduct}
                            )}
                            onClick={handleLoadMore}
                        >
                            <FormattedMessage
                                defaultMessage="Load more +{nbRemainingProduct}"
                                id="product_list.pagination.load_more"
                                values={{nbRemainingProduct}}
                            />
                        </Button>
                    </Box>
                )}

                {!isHydrated() && (
                    <Box mt="20px">
                        {previousPageParam && (
                            <Link variant="link" mr="20px" href={previousPageParam}>
                                <FormattedMessage
                                    defaultMessage="Previous page"
                                    id="pagination.link.prev.assistive_msg"
                                />
                            </Link>
                        )}

                        {nextPageParam && (
                            <Link variant="link" href={nextPageParam}>
                                <FormattedMessage
                                    defaultMessage="Next page"
                                    id="pagination.link.next.assistive_msg"
                                />
                            </Link>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

ProductListClassicSubset.propTypes = {
    subSet: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

export default ProductListClassicSubset
